import { useAppSelector, useAppDispatch } from "@/services/hooks";

import React, { useCallback, useEffect, useMemo, useState } from "react";

import { ToastContainer } from "react-toastify";
import { Link } from "react-router";
import NotificationAlertOptions from "@/layouts/Alerts";
import ConfirmDelete from "@/components/ConfirmDelete";
import ReferrersDialog from "@/components/ReferrersDialog";
import { isDeleteRefusal, refusalMessage } from "@/services/deleteRefusal";
import ListingPagesAffected from "./ListingPagesAffected";
import {
  getAllProducts,
  clearAllProducts,
  getProductsByCountryCode,
  deleteProduct,
  clearProductById,
  clearMessage,
  fetchProductListingPages,
} from "@/services/productServices/productActions";
import { clearErrors } from "@/services/errorServices/errorAction";
import { useCurrentPermission } from "@/services/userServices/useCurrentPermission";
import GlobalProductsView from "./GlobalProductsView";
import { Button, Media } from "reactstrap";

export default function GlobalProductsContainer() {
  const dispatch = useAppDispatch();

  const productsSlice = useAppSelector((s) => s.products);
  const permission = useCurrentPermission();

  const products = productsSlice?.products_country_code || [];
  const [alert, setAlert] = useState<React.ReactNode>(null);

  useEffect(() => {
    dispatch(getAllProducts());
    dispatch(getProductsByCountryCode());
    dispatch(clearProductById());
    return () => {
      dispatch(clearAllProducts());
    };
  }, [dispatch]);

  const hideAlert = useCallback(() => setAlert(null), []);

  // SCRUM-1303: the confirmation names the listing pages the SCRUM-1301 cascade
  // will change (every site — a delete has no site filter), through the house
  // dialog, which owns the ordering the old inline copy re-implemented: close,
  // await the delete, and only then toast success (SCRUM-1021 / SCRUM-1356). A
  // failure still rejects and is surfaced centrally by appErrorMiddleware.
  // The lookup failing is not "no pages": say so and open no dialog.
  const questionAlert = useCallback(
    async (e: { preventDefault?: () => void } | undefined, id: string) => {
      e?.preventDefault?.();
      let pages;
      try {
        pages = await fetchProductListingPages(id);
      } catch {
        NotificationAlertOptions(
          "error",
          "Could not look up the listing pages this product is on. Nothing was changed."
        );
        return;
      }
      setAlert(
        <ConfirmDelete
          title="Would you like to remove this product?"
          onClose={hideAlert}
          showCancel
          onConfirm={() => dispatch(deleteProduct(id)).unwrap()}
          onSuccess={() =>
            NotificationAlertOptions("success", "Product Deleted with success")
          }
          onError={(err) => {
            // SCRUM-2353: refused while product pages still show it — the list
            // instead of a toast (SCRUM-2352); other failures stay central.
            if (!isDeleteRefusal(err)) return;
            setAlert(
              <ReferrersDialog
                message={refusalMessage(err)}
                referrers={err.referrers}
                onClose={hideAlert}
              />
            );
          }}
        >
          <ListingPagesAffected pages={pages} />
        </ConfirmDelete>
      );
    },
    [dispatch, hideAlert]
  );

  const columns = useMemo(() => {
    const base = [
      { accessorKey: "country_code", header: "Country Code" },
      {
        accessorKey: "productImg",
        header: "Image",
        cell: ({ row }) => (
          <div>
            <Media className="align-items-center">
              <img
                alt={row.alt_pic}
                className="avatar rounded-circle mr-3"
                src={row.original.productImg}
              />
            </Media>
          </div>
        ),
      },
      { accessorKey: "title", header: "Title" },
      // SCRUM-1447: the Active column is gone. SCRUM-312 removed the control that set
      // this flag, so the column showed editors a status no screen could change. The
      // field itself is deliberately kept in the data (SCRUM-312 scope note) and the
      // 70 products that carry `active: false` are SCRUM-1444's editorial decision.
    ];

    if (permission.edit || permission.publish) {
      base.push({
        accessorKey: "link",
        header: "ACTION",
        cell: ({ row }) => (
          <div>
            <Button
              className="mr-2"
              color="primary"
              to={`/admin/update-product/${row.original._id}/${row.original.country_code}`}
              size="sm"
              tag={Link}
            >
              <i className="fas fa-pen-to-square" />
              <span className="ml-1">Edit</span>
            </Button>
            {/* SCRUM-1633: the Tag button is gone — its manage-tags route was removed
                with the tag screens (SCRUM-1624) and the click landed on not-found. */}
            <Button
              className="mr-2"
              color="warning"
              to={`/admin/ads/${row.original._id}`}
              size="sm"
              tag={Link}
            >
              <i className="fas fa-rectangle-ad" />
              <span className="ml-1">Ads</span>
            </Button>
            <Button
              className="mr-2"
              color="danger"
              onClick={(e) => questionAlert(e, row.original._id)}
              size="sm"
            >
              <i className="fas fa-trash" />
              <span className="ml-1">Delete</span>
            </Button>
          </div>
        ),
      });
    }
    return base;
  }, [permission, questionAlert]);

  const sortedData = useMemo(
    () =>
      [...products].sort((a, b) => (a._id > b._id ? -1 : b._id > a._id ? 1 : 0)),
    [products]
  );

  return (
    <>
      <div className="rna-wrapper">
        <ToastContainer />
      </div>
      {alert}
      <GlobalProductsView data={sortedData} columns={columns} />
    </>
  );
}
